import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatBRL, isStale, daysSince } from '@/lib/utils';
import type { Candidate, CandidateProfile, FunnelStatus } from '@/types';

const STATUS_OPTIONS: { value: FunnelStatus; label: string }[] = [
  { value: 'prospeccao',      label: 'Prospecção'        },
  { value: 'r1',              label: 'R1 - Café'         },
  { value: 'r2_r3',          label: 'R2/R3 - Entrevista' },
  { value: 'proposta',        label: 'Proposta'          },
  { value: 'fechado_ganho',   label: 'Fechado - Ganho'   },
  { value: 'fechado_perdido', label: 'Fechado - Perdido' },
];

interface Props {
  candidate: Candidate;
  onClick: () => void;
  onStatusChange?: (id: string, status: FunnelStatus) => void;
}

const PROFILE_LABELS: Record<CandidateProfile, string> = {
  farmer: 'Farmer', hunter: 'Hunter', private: 'Private', alta_renda: 'Alta Renda',
};

const PROFILE_VARIANTS: Record<CandidateProfile, 'blue' | 'orange' | 'purple' | 'green'> = {
  farmer: 'blue', hunter: 'orange', private: 'purple', alta_renda: 'green',
};

function scoreColor(score: number) {
  if (score >= 70) return '#3A7A58';
  if (score >= 40) return '#B07C18';
  return '#A83830';
}

export function CandidateCard({ candidate, onClick, onStatusChange }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: candidate.id });

  const stale = isStale(candidate.last_interaction_date);
  const days  = candidate.last_interaction_date ? daysSince(candidate.last_interaction_date) : null;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={[
        'group rounded border bg-bg-surface px-3 py-2.5 cursor-pointer select-none transition-all',
        'hover:shadow-card hover:border-border-subtle/80',
        stale  ? 'border-status-red/30' : 'border-border',
        isDragging ? 'opacity-50 shadow-lift rotate-1 scale-[1.02]' : 'shadow-card',
      ].join(' ')}
    >
      {/* Name + score */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="text-sm font-medium text-text-primary truncate leading-snug">
          {candidate.name}
        </p>
        <span
          className="font-mono text-xs font-semibold shrink-0 leading-none mt-0.5"
          style={{ color: scoreColor(candidate.score) }}
        >
          {candidate.score}
        </span>
      </div>

      {/* Institution */}
      <p className="text-xs text-text-muted truncate mb-2">{candidate.current_institution}</p>

      {/* Footer */}
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-2xs text-text-secondary tabular-nums">
          {formatBRL(candidate.aum_cents)}
        </span>
        <Badge variant={PROFILE_VARIANTS[candidate.profile]}>
          {PROFILE_LABELS[candidate.profile]}
        </Badge>
      </div>

      {stale && days !== null && (
        <div className="mt-2 flex items-center gap-1 text-2xs text-status-red">
          <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
          <span>{days}d sem contato</span>
        </div>
      )}

      {/* Stage picker — tap to change status (reliable on mobile) */}
      {onStatusChange && (
        <select
          value={candidate.funnel_status}
          onChange={(e) => {
            e.stopPropagation();
            onStatusChange(candidate.id, e.target.value as FunnelStatus);
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="mt-2 w-full rounded border border-border bg-bg-elevated px-2 py-1 text-2xs text-text-secondary appearance-none cursor-pointer focus:outline-none focus:border-accent/50 hover:border-border-subtle"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
    </div>
  );
}
